import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Tooltip,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Pencil, Radio, Search, MapPin, Wifi, WifiOff, X, ClipboardPaste, PauseCircle, PlayCircle } from "lucide-react";
import ArthaTextField from "../../fields/ArthaTextField";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import {
  saveBeaconAPI,
  fetchBeaconApi,
  updateBeaconAPI,
  fetchLocationApi,
  fetchSubOrgDropdown,
  saveBeaconRoomAPI,
  fetchBeaconRoomApi,
} from "../../actioncreators/actioncreators";
import { useSelector } from "react-redux";
import { useSuspension } from "../../contexts/SuspensionContext";
import "./dashboard.css";

const DEFAULT_RSSI_THRESHOLD = -75;
const ACCENT = "#fd610a";

// One look for every text input in the Register/Edit Beacon dialog. The shared
// ArthaTextField is 32px tall with 12px text and a 16px placeholder, while this
// dialog's MUI Selects are 40px / 14px - mixing them left the fields misaligned
// with inconsistent text sizes. These values match the Selects (40px height, 14px
// text, 12px side padding, same label position and outline colours), and give
// helper text one size/colour. Applied via `sx` so the shared ArthaTextField,
// which the rest of the app relies on, is left untouched.
const dialogFieldSx = {
  "& .MuiInputBase-root": { height: "40px", fontSize: "0.875rem", padding: 0 },
  "& .MuiInputBase-input": {
    height: "auto",
    padding: "8px 12px",
    fontSize: "0.875rem",
    lineHeight: 1.4375,
    "&::placeholder": { fontSize: "0.875rem", opacity: 0.6 },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    color: "#666",
    transform: "translate(14px, 10px) scale(1)",
    "&.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.75)" },
    "&.Mui-focused": { color: ACCENT },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d0d0d0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#999" },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT, borderWidth: "2px" },
  "& .MuiFormHelperText-root": {
    fontSize: "12px",
    lineHeight: 1.4,
    margin: "4px 2px 0",
    color: "#6b7280",
  },
  "& .MuiFormHelperText-root.Mui-error": { color: "#d32f2f" },
};

const initialFormData = {
  beacon_name: "",
  uuid: "",
  major: "",
  minor: "",
  location_id: "",
  room_id: "",
  sub_org_id: "",
  rssi_threshold: DEFAULT_RSSI_THRESHOLD,
  required_stay_minutes: "",
  requires_checkin: true,
};

// Standard 8-4-4-4-12 UUID shape, case-insensitive.
const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
// Same shape, unanchored, for pulling a UUID out of a larger block of pasted text.
const UUID_SEARCH_PATTERN = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;

// Parses whatever a user pastes from a BLE scanner app (e.g. nRF Connect) -
// a raw advertisement dump, a device details screen, or just three loosely
// typed values - and pulls out a UUID plus optional Major/Minor. Tolerant of
// varied label formats ("Major: 1", "major=1", "\"major\": 1", "Major 1")
// since scanner apps differ in what exactly gets copied.
const parseScanText = (text) => {
  if (!text || !text.trim()) return null;

  const uuidMatch = text.match(UUID_SEARCH_PATTERN);
  const uuid = uuidMatch ? uuidMatch[0].toUpperCase() : null;

  const findLabeledNumber = (label) => {
    // Matches: Major: 1 | major=1 | "major": 1 | Major - 1 | Major 1
    const re = new RegExp(`${label}["']?\\s*[:=-]?\\s*(\\d{1,5})`, "i");
    const match = text.match(re);
    if (!match) return null;
    const num = parseInt(match[1], 10);
    return Number.isFinite(num) && num >= 0 && num <= 65535 ? num : null;
  };

  const major = findLabeledNumber("major");
  const minor = findLabeledNumber("minor");

  if (!uuid && major === null && minor === null) return null;

  return { uuid, major, minor };
};

const Beacons = () => {
  const { isSuspended } = useSuspension();

  // Super Admin org filtering support - same convention as AddWorkLocation.jsx
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const loginResponse = useSelector((state) => state.loginResponse);
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === 'SUPER_ADMIN' || loginResponse?.roleName?.toUpperCase() === 'SUPER_ADMIN';
  const isAdmin = loginResponse?.role_name?.toUpperCase() === 'ADMIN' || loginResponse?.roleName?.toUpperCase() === 'ADMIN';
  const userRole = loginResponse?.role_name || loginResponse?.roleName || '';
  const superAdminSelectedSubOrgId = superAdminSelectedOrgData?.type === 'sub_org' ? superAdminSelectedOrgData?.sub_org_id : null;

  const [beacons, setBeacons] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [subOrganizations, setSubOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBeaconId, setSelectedBeaconId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [uuidTouched, setUuidTouched] = useState(false);
  const [showPasteScan, setShowPasteScan] = useState(false);
  const [pasteScanText, setPasteScanText] = useState("");
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const [isSavingRoom, setIsSavingRoom] = useState(false);

  useEffect(() => {
    loadAll();
    // Admin drilling into/out of a sub-org via the header dropdown only
    // changes superAdminSelectedSubOrgId (superAdminSelectedOrgId stays the
    // Admin's own org_id throughout) - without this dependency, switching
    // sub-orgs never re-fetched and the page kept showing stale data.
  }, [superAdminSelectedOrgId, superAdminSelectedSubOrgId]);

  const loadAll = async () => {
    setIsLoading(true);
    await Promise.all([fetchBeacons(), loadLocations(), loadRooms(), loadSubOrganizations()]);
    setIsLoading(false);
  };

  const loadSubOrganizations = async () => {
    try {
      // Scope the dropdown to the org a Super Admin has selected; without this the
      // backend falls back to the Super Admin's own JWT org and the list always
      // shows that org's sub-orgs regardless of the selection.
      const response = await fetchSubOrgDropdown(isSuperAdmin ? superAdminSelectedOrgId : null);
      setSubOrganizations(
        response?.data?.businessStatusCode === 2 ? (response.data.data || []) : []
      );
    } catch (error) {
      console.error('Failed to load sub-organizations:', error);
      setSubOrganizations([]);
    }
  };

  const loadLocations = async () => {
    const requestBody = {
      entity_type: "location",
      pagenumber: 1,
      parameter: [
        { columnName: "status", condition: "=", value: "ACTIVE" },
      ],
    };

    if (isSuperAdmin && superAdminSelectedOrgId) {
      requestBody.org_id = superAdminSelectedOrgId;
      if (superAdminSelectedSubOrgId) {
        requestBody.sub_org_id = superAdminSelectedSubOrgId;
      }
    } else if (isAdmin && superAdminSelectedSubOrgId) {
      requestBody.sub_org_id = superAdminSelectedSubOrgId;
    }

    try {
      const response = await fetchLocationApi(requestBody);
      const { businessStatusCode, viewListData } = response || {};
      setLocations(businessStatusCode === 2 && viewListData ? viewListData : []);
    } catch (error) {
      console.error('Failed to load locations:', error);
      setLocations([]);
    }
  };

  const loadRooms = async () => {
    const requestBody = {
      entity_type: "beacon_room",
      pagenumber: 1,
      parameter: [
        { columnName: "status", condition: "=", value: "ACTIVE" },
      ],
    };

    if (isSuperAdmin && superAdminSelectedOrgId) {
      requestBody.org_id = superAdminSelectedOrgId;
      if (superAdminSelectedSubOrgId) {
        requestBody.sub_org_id = superAdminSelectedSubOrgId;
      }
    } else if (isAdmin && superAdminSelectedSubOrgId) {
      requestBody.sub_org_id = superAdminSelectedSubOrgId;
    }

    try {
      const response = await fetchBeaconRoomApi(requestBody);
      const { businessStatusCode, viewListData } = response || {};
      setRooms(businessStatusCode === 2 && viewListData ? viewListData : []);
    } catch (error) {
      console.error('Failed to load rooms/areas:', error);
      setRooms([]);
    }
  };

  const fetchBeacons = async () => {
    const requestBody = {
      entity_type: "beacon",
      pagenumber: 1,
      // No status filter - suspended beacons must still appear (so they can
      // be reactivated), just visually distinguished. See handleSuspendToggle.
      parameter: [],
    };

    if (isSuperAdmin && superAdminSelectedOrgId) {
      requestBody.org_id = superAdminSelectedOrgId;
      if (superAdminSelectedSubOrgId) {
        requestBody.sub_org_id = superAdminSelectedSubOrgId;
      }
    } else if (isAdmin && superAdminSelectedSubOrgId) {
      requestBody.sub_org_id = superAdminSelectedSubOrgId;
    }

    try {
      const response = await fetchBeaconApi(requestBody);
      const { businessStatusCode, viewListData } = response || {};
      setBeacons(businessStatusCode === 2 && viewListData ? viewListData : []);
    } catch (error) {
      console.error("Error fetching beacons:", error);
      toast.error(error?.response?.data?.message || "Failed to load beacons");
      setBeacons([]);
    }
  };

  const getLocationName = (locationId) => {
    if (!locationId) return null;
    const loc = locations.find((l) => l.location_id === locationId);
    return loc ? loc.location_name : null;
  };

  const getRoomName = (roomId) => {
    if (!roomId) return null;
    const room = rooms.find((r) => r.beacon_room_id === roomId);
    return room ? room.room_name : null;
  };

  const getRoomsForLocation = (locationId) => {
    if (!locationId) return [];
    return rooms.filter((r) => r.location_id === locationId);
  };

  const getSubOrg = (subOrgId) => {
    if (!subOrgId) return null;
    return subOrganizations.find((s) => s.sub_org_id === subOrgId) || null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "uuid") setUuidTouched(true);
    if (name === "location_id") {
      // Room is scoped to a location - changing the location invalidates
      // whatever room was previously selected (it almost certainly belongs
      // to the old location).
      setFormData((prev) => ({ ...prev, location_id: value, room_id: "" }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRoom = async () => {
    if (!newRoomName.trim() || !formData.location_id) return;
    setIsSavingRoom(true);
    try {
      const columns = {
        room_name: newRoomName.trim(),
        location_id: formData.location_id,
      };
      if (formData.sub_org_id) {
        columns.sub_org_id = formData.sub_org_id;
      }
      if (isSuperAdmin && superAdminSelectedOrgId) {
        columns.org_id = superAdminSelectedOrgId;
      }
      const requestBody = {
        entity_type: "beacon_room",
        comment: "comment added",
        columns,
      };
      const response = await saveBeaconRoomAPI(requestBody);
      if (response && response.businessStatusCode === 2) {
        toast.success("Room/Area added");
        setNewRoomName("");
        setShowAddRoom(false);
        await loadRooms();
        if (response.id) {
          setFormData((prev) => ({ ...prev, room_id: response.id }));
        }
      } else {
        toast.error(response?.message || "Failed to add room/area");
      }
    } catch (error) {
      console.error("Error adding room/area:", error);
      toast.error(error?.response?.data?.message || "An error occurred while adding the room/area");
    } finally {
      setIsSavingRoom(false);
    }
  };

  const handleApplyPastedScan = () => {
    const parsed = parseScanText(pasteScanText);
    if (!parsed) {
      toast.error("Couldn't find a UUID or Major/Minor in that text. Paste the beacon's scan details from nRF Connect (or similar).");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      uuid: parsed.uuid || prev.uuid,
      major: parsed.major !== null ? String(parsed.major) : prev.major,
      minor: parsed.minor !== null ? String(parsed.minor) : prev.minor,
    }));
    if (parsed.uuid) setUuidTouched(true);

    const found = [
      parsed.uuid ? "UUID" : null,
      parsed.major !== null ? "Major" : null,
      parsed.minor !== null ? "Minor" : null,
    ].filter(Boolean);
    toast.success(`Filled in: ${found.join(", ")}`);
    setPasteScanText("");
    setShowPasteScan(false);
  };

  const handleAddBeacon = (prefillLocationId) => {
    setFormData({
      ...initialFormData,
      location_id: prefillLocationId || "",
      room_id: "",
      // Pre-fill from the Super Admin's current sub-org drilldown, if any -
      // same convention as AddWorkLocation.jsx.
      sub_org_id: superAdminSelectedSubOrgId || "",
    });
    setShowPasteScan(false);
    setPasteScanText("");
    setShowAddRoom(false);
    setNewRoomName("");
    setUuidTouched(false);
    setIsEditing(false);
    setSelectedBeaconId(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (beacon) => {
    setIsEditing(true);
    setFormData({
      beacon_name: beacon.beacon_name || "",
      uuid: beacon.uuid || "",
      major: beacon.major ?? "",
      minor: beacon.minor ?? "",
      location_id: beacon.location_id || "",
      room_id: beacon.room_id || "",
      sub_org_id: beacon.sub_org_id || "",
      rssi_threshold: beacon.rssi_threshold ?? DEFAULT_RSSI_THRESHOLD,
      required_stay_minutes: beacon.required_stay_minutes ?? "",
      requires_checkin: beacon.requires_checkin ?? true,
    });
    setUuidTouched(true);
    setShowAddRoom(false);
    setNewRoomName("");
    setSelectedBeaconId(beacon.beacon_id);
    setIsDialogOpen(true);
  };

  // Beacons can never be hard-deleted once registered, only suspended
  // (status -> INACTIVE) or reactivated (status -> ACTIVE) - a suspended
  // beacon's historical sightings/presence sessions stay intact and it can
  // be brought back later without re-registering.
  const handleSuspendToggle = async (beacon) => {
    const suspending = beacon.status === "ACTIVE";
    const action = suspending ? "Suspend" : "Reactivate";
    if (!window.confirm(`${action} "${beacon.beacon_name}"?`)) return;

    const payload = {
      entity_type: "beacon",
      comment: "comment added",
      old_data: { beacon_id: beacon.beacon_id },
      new_data: { beacon_id: beacon.beacon_id, status: suspending ? "INACTIVE" : "ACTIVE" },
    };
    try {
      const response = await updateBeaconAPI(payload);
      if (response && response.businessStatusCode === 2) {
        toast.success(suspending ? "Beacon suspended" : "Beacon reactivated");
        fetchBeacons();
      } else {
        toast.error(response?.message || `Failed to ${action.toLowerCase()} beacon`);
      }
    } catch (error) {
      console.error(`Error ${action.toLowerCase()}ing beacon:`, error);
      toast.error(error?.response?.data?.message || `An error occurred while trying to ${action.toLowerCase()} the beacon`);
    }
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
  };

  const isUuidValid = formData.uuid.trim() === "" || UUID_PATTERN.test(formData.uuid.trim());

  // Another beacon in the loaded list with the same UUID + Major + Minor (UUID
  // compared case-insensitively; a blank Major/Minor only equals another blank
  // one). Early feedback only - the backend re-checks and is authoritative, and
  // the loaded list may not include every beacon in the org.
  const duplicateBeacon = useMemo(() => {
    const uuid = formData.uuid.trim().toUpperCase();
    if (!uuid || !UUID_PATTERN.test(uuid)) return null;
    const toKey = (v) => (v === "" || v === null || v === undefined ? "" : String(Number(v)));
    const major = toKey(formData.major);
    const minor = toKey(formData.minor);
    return (
      beacons.find(
        (b) =>
          b.beacon_id !== selectedBeaconId &&
          (b.uuid || "").toUpperCase() === uuid &&
          toKey(b.major) === major &&
          toKey(b.minor) === minor
      ) || null
    );
  }, [beacons, formData.uuid, formData.major, formData.minor, selectedBeaconId]);

  const handleSave = async () => {
    if (formData.uuid.trim() && !UUID_PATTERN.test(formData.uuid.trim())) {
      toast.error("UUID must be in the standard format, e.g. E4CCDBE2-2A2A-2FA3-E911-F3CDF8B4DDCC");
      return;
    }
    if (duplicateBeacon) {
      toast.error(`A beacon with this UUID, Major and Minor is already registered as "${duplicateBeacon.beacon_name}".`);
      return;
    }

    const columns = {
      beacon_name: formData.beacon_name?.trim(),
      uuid: formData.uuid?.trim().toUpperCase(),
    };

    if (formData.major !== "" && formData.major !== null && formData.major !== undefined) {
      columns.major = Number(formData.major);
    }
    if (formData.minor !== "" && formData.minor !== null && formData.minor !== undefined) {
      columns.minor = Number(formData.minor);
    }
    if (formData.location_id) {
      columns.location_id = formData.location_id;
    }
    if (formData.room_id) {
      columns.room_id = formData.room_id;
    }
    if (formData.sub_org_id) {
      columns.sub_org_id = formData.sub_org_id;
    }
    columns.rssi_threshold = formData.rssi_threshold !== "" && formData.rssi_threshold !== null && formData.rssi_threshold !== undefined
      ? Number(formData.rssi_threshold)
      : DEFAULT_RSSI_THRESHOLD;
    // Blank = no stay monitoring for this beacon (stored as NULL on create;
    // sent as null on update so clearing the field actually clears it).
    const requiredStay = formData.required_stay_minutes;
    columns.required_stay_minutes =
      requiredStay !== "" && requiredStay !== null && requiredStay !== undefined && Number(requiredStay) > 0
        ? Math.floor(Number(requiredStay))
        : null;
    columns.requires_checkin = Boolean(formData.requires_checkin);

    // When Super Admin is operating on a selected organisation, pass org_id in the
    // columns payload so the backend can override the JWT org with the target org.
    if (isSuperAdmin && superAdminSelectedOrgId) {
      columns.org_id = superAdminSelectedOrgId;
    }

    const requestBody = {
      entity_type: "beacon",
      comment: "comment added",
      columns,
    };

    const newData = {
      beacon_id: selectedBeaconId,
      beacon_name: formData.beacon_name?.trim(),
      uuid: formData.uuid?.trim().toUpperCase(),
      // null (not undefined) so clearing Major/Minor in the form actually clears
      // them - an undefined key is dropped from the JSON and left unchanged.
      major: columns.major ?? null,
      minor: columns.minor ?? null,
      location_id: formData.location_id || null,
      room_id: formData.room_id || null,
      sub_org_id: formData.sub_org_id || null,
      rssi_threshold: columns.rssi_threshold,
      required_stay_minutes: columns.required_stay_minutes,
      requires_checkin: columns.requires_checkin,
      status: "ACTIVE",
    };

    const requestBody1 = {
      entity_type: "beacon",
      comment: "comment added",
      // The backend rejects an update whose old_data is empty (treats it the
      // same as "no data provided" - see Backend/helpers/updateEntity.go).
      // beacon_id alone is enough to satisfy that check; the backend
      // identifies the row to update via new_data's beacon_id regardless.
      old_data: { beacon_id: selectedBeaconId },
      new_data: newData,
    };

    try {
      let saveResponse;
      if (selectedBeaconId) {
        saveResponse = await updateBeaconAPI(requestBody1);
      } else {
        saveResponse = await saveBeaconAPI(requestBody);
      }

      if (saveResponse && saveResponse.businessStatusCode === 2) {
        toast.success(selectedBeaconId ? "Beacon updated" : "Beacon registered");
        fetchBeacons();
        setIsDialogOpen(false);
      } else {
        toast.error(saveResponse?.message || "Failed to save beacon");
      }
    } catch (error) {
      console.error("Error saving beacon:", error);
      toast.error(error?.response?.data?.message || "An error occurred while saving the beacon");
    }
  };

  const isSaveDisabled = () => {
    return !(formData?.beacon_name?.trim() && formData?.uuid?.trim() && isUuidValid) || Boolean(duplicateBeacon);
  };

  // Locations that don't yet have an active beacon registered - the whole
  // point of this screen is closing this list to zero across all locations.
  const unbeaconedLocations = useMemo(() => {
    const coveredLocationIds = new Set(beacons.map((b) => b.location_id).filter(Boolean));
    return locations.filter((loc) => !coveredLocationIds.has(loc.location_id));
  }, [beacons, locations]);

  const filteredBeacons = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return beacons.filter((b) => {
      if (classFilter && b.sub_org_id !== classFilter) return false;
      if (locationFilter && b.location_id !== locationFilter) return false;
      if (!term) return true;
      const locName = getLocationName(b.location_id) || "";
      const subOrg = getSubOrg(b.sub_org_id);
      const haystack = [b.beacon_name, b.uuid, locName, subOrg?.sub_org_name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beacons, searchTerm, classFilter, locationFilter, locations, subOrganizations]);

  // Show "Select Organization" message if Super Admin hasn't selected an org
  if (isSuperAdmin && !superAdminSelectedOrgId) {
    return (
      <div className="p-4" style={{ backgroundColor: "#EFF0F4", minHeight: "100vh" }}>
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Beacons</h1>
          <p className="text-sm text-gray-1000">Super Admin View</p>
        </div>
        <div className="d-flex flex-column align-items-center justify-content-center bg-white rounded-lg shadow-sm p-5 mt-4">
          <Radio size={72} style={{ color: "#ddd" }} className="mb-3" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Select an Organization</h2>
          <p className="text-gray-1000 text-center" style={{ maxWidth: 420 }}>
            Please select an organization from the dropdown in the header to view and manage its beacons.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid m-0 p-0" style={{ backgroundColor: "#eff0f4", minHeight: "100vh" }}>
      {/* Header */}
      <div
        className="p-3 border-bottom"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: "12px" }}>
          <div>
            <h5 className="fw-bold m-0 d-flex align-items-center" style={{ gap: "8px" }}>
              <Radio size={20} style={{ color: ACCENT }} />
              Beacons
            </h5>
            <p className="text-secondary m-0 mt-1" style={{ fontSize: "13px" }}>
              Register a BLE beacon for each location so student attendance can be tracked location-by-location.
            </p>
          </div>
          <div
            className="btn d-flex align-items-center"
            style={{
              height: "38px",
              padding: "0 18px",
              gap: "6px",
              fontSize: "13px",
              fontWeight: 600,
              backgroundColor: isSuspended ? "#ccc" : ACCENT,
              color: "white",
              cursor: isSuspended ? "not-allowed" : "pointer",
              opacity: isSuspended ? 0.6 : 1,
              borderRadius: "20px",
              whiteSpace: "nowrap",
            }}
            onClick={isSuspended ? undefined : () => handleAddBeacon()}
            title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
          >
            + Register Beacon
          </div>
        </div>

        {/* Stat strip */}
        <div className="d-flex flex-wrap mt-3" style={{ gap: "10px" }}>
          <StatChip icon={<Wifi size={14} />} label="Registered" value={beacons.length} />
          <StatChip
            icon={<WifiOff size={14} />}
            label="Locations without a beacon"
            value={unbeaconedLocations.length}
            warn={unbeaconedLocations.length > 0}
          />
        </div>
      </div>

      {/* Toolbar: search + class filter */}
      <div className="p-3 d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
        <ArthaTextField
          size="small"
          placeholder="Search by beacon name, location, class, or UUID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ minWidth: 280, backgroundColor: "#fff", borderRadius: "20px" }}
          InputProps={{
            style: { borderRadius: "20px" },
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} style={{ color: "#999" }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm("")}>
                  <X size={14} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {subOrganizations.length > 0 && (
          <FormControl size="small" style={{ minWidth: 220, backgroundColor: "#fff", borderRadius: "20px" }}>
            <Select
              displayEmpty
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              sx={{
                fontSize: "13px",
                borderRadius: "20px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
              }}
              renderValue={(val) => {
                if (!val) return <span style={{ color: "#999" }}>All classes</span>;
                const s = getSubOrg(val);
                return s ? `${s.sub_org_name} (${s.sub_org_code})` : "All classes";
              }}
            >
              <MenuItem value="">All classes</MenuItem>
              {subOrganizations.map((s) => (
                <MenuItem key={s.sub_org_id} value={s.sub_org_id}>
                  {s.sub_org_name} ({s.sub_org_code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {locations.length > 0 && (
          <FormControl size="small" style={{ minWidth: 220, backgroundColor: "#fff", borderRadius: "20px" }}>
            <Select
              displayEmpty
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              sx={{
                fontSize: "13px",
                borderRadius: "20px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
              }}
              renderValue={(val) => {
                if (!val) return <span style={{ color: "#999" }}>All locations</span>;
                return getLocationName(val) || "All locations";
              }}
            >
              <MenuItem value="">All locations</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.location_id} value={loc.location_id}>
                  {loc.location_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <div className="text-secondary" style={{ fontSize: "12px", marginLeft: "auto" }}>
          {filteredBeacons.length} of {beacons.length} beacon{beacons.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* Missing-coverage banner */}
      {!isLoading && unbeaconedLocations.length > 0 && (
        <div className="px-3 pb-2">
          <div
            className="p-2 px-3 d-flex align-items-center flex-wrap"
            style={{
              backgroundColor: "#fff7ed",
              border: "1px solid #fed7aa",
              borderRadius: "10px",
              gap: "8px",
              fontSize: "12.5px",
              color: "#9a3412",
            }}
          >
            <WifiOff size={14} />
            <span className="fw-bold">{unbeaconedLocations.length} location{unbeaconedLocations.length === 1 ? "" : "s"} still need a beacon:</span>
            {unbeaconedLocations.slice(0, 6).map((loc) => (
              <Chip
                key={loc.location_id}
                label={loc.location_name}
                size="small"
                onClick={() => handleAddBeacon(loc.location_id)}
                clickable
                style={{ backgroundColor: "#ffedd5", color: "#9a3412", fontWeight: 600, cursor: "pointer" }}
              />
            ))}
            {unbeaconedLocations.length > 6 && (
              <span>+{unbeaconedLocations.length - 6} more</span>
            )}
          </div>
        </div>
      )}

      {/* Beacon cards */}
      <div className="p-3 pt-0">
        {isLoading ? (
          <div className="text-center p-5 bg-white rounded shadow-sm">
            <p className="text-secondary mb-0">Loading beacons…</p>
          </div>
        ) : beacons.length === 0 ? (
          <div className="text-center p-5 bg-white rounded shadow-sm">
            <Radio size={64} style={{ color: "#ddd" }} className="mb-3" />
            <p className="fw-bold mb-1">No beacons registered yet</p>
            <p className="text-secondary mb-3">Register your first beacon to start tracking attendance location-by-location.</p>
            <div
              className="btn d-inline-flex align-items-center"
              style={{
                gap: "6px",
                fontSize: "13px",
                fontWeight: 600,
                backgroundColor: ACCENT,
                color: "white",
                borderRadius: "20px",
                padding: "8px 20px",
                cursor: "pointer",
              }}
              onClick={() => handleAddBeacon()}
            >
              + Register Beacon
            </div>
          </div>
        ) : filteredBeacons.length === 0 ? (
          <div className="text-center p-5 bg-white rounded shadow-sm">
            <p className="text-secondary mb-0">No beacons match your search.</p>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "14px",
            }}
          >
            {filteredBeacons.map((beacon) => {
              const locName = getLocationName(beacon.location_id);
              const roomName = getRoomName(beacon.room_id);
              return (
                <div
                  key={beacon.beacon_id}
                  className="bg-white shadow-sm"
                  style={{
                    borderRadius: "14px",
                    border: "1px solid #eee",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: "10px",
                          backgroundColor: "#fff1e8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Radio size={17} style={{ color: ACCENT }} />
                      </div>
                      <div>
                        <div className="fw-bold" style={{ fontSize: "14px", lineHeight: 1.2 }}>
                          {beacon.beacon_name}
                        </div>
                        <Chip
                          label={beacon.status || "ACTIVE"}
                          size="small"
                          style={{
                            height: "18px",
                            fontSize: "10px",
                            fontWeight: 700,
                            marginTop: "3px",
                            backgroundColor: beacon.status === "ACTIVE" ? "#e6f4ea" : "#f5f5f5",
                            color: beacon.status === "ACTIVE" ? "#1e7e34" : "#666",
                          }}
                        />
                      </div>
                    </div>
                    <div className="d-flex" style={{ gap: "2px" }}>
                      <Tooltip title="Edit" arrow>
                        <IconButton size="small" onClick={isSuspended ? undefined : () => handleEditClick(beacon)} disabled={isSuspended}>
                          <Pencil size={14} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={beacon.status === "ACTIVE" ? "Suspend" : "Reactivate"} arrow>
                        <IconButton size="small" onClick={isSuspended ? undefined : () => handleSuspendToggle(beacon)} disabled={isSuspended}>
                          {beacon.status === "ACTIVE" ? <PauseCircle size={14} /> : <PlayCircle size={14} />}
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  <div
                    style={{
                      fontFamily: "monospace",
                      fontSize: "11px",
                      color: "#888",
                      backgroundColor: "#fafafa",
                      borderRadius: "6px",
                      padding: "6px 8px",
                      wordBreak: "break-all",
                    }}
                  >
                    {beacon.uuid}
                  </div>

                  <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                    <MiniStat label="Major" value={beacon.major ?? "—"} />
                    <MiniStat label="Minor" value={beacon.minor ?? "—"} />
                    <MiniStat label="RSSI" value={beacon.rssi_threshold ?? DEFAULT_RSSI_THRESHOLD} />
                  </div>

                  <Chip
                    size="small"
                    label={
                      beacon.requires_checkin === false
                        ? "No identity check"
                        : "Requires identity check"
                    }
                    sx={{
                      alignSelf: "flex-start",
                      fontSize: "11px",
                      height: "22px",
                      backgroundColor: beacon.requires_checkin === false ? "#fff3e0" : "#e8f5e9",
                      color: beacon.requires_checkin === false ? "#e65100" : "#2e7d32",
                    }}
                  />

                  <div className="d-flex flex-column" style={{ gap: "4px", fontSize: "12.5px", borderTop: "1px solid #f2f2f2", paddingTop: "8px" }}>
                    <div className="d-flex align-items-center" style={{ gap: "6px", color: locName ? "#333" : "#bbb" }}>
                      <MapPin size={13} />
                      {locName || "No location assigned"}
                      {roomName && <span style={{ color: "#999" }}> &rsaquo; {roomName}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialog for Adding/Editing Beacon */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={false}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '14px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            position: 'relative',
            zIndex: 1300,
            margin: '32px',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1299,
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2, pt: 3, px: 3,
          fontSize: '1.35rem',
          fontWeight: 700,
          color: '#1a1a1a',
          borderBottom: '1px solid #e0e0e0',
        }}>
          {isEditing ? "Edit Beacon" : "Register a New Beacon"}
          {!isEditing && (
            <div style={{ fontSize: "12.5px", fontWeight: 400, color: "#888", marginTop: "4px" }}>
              Configure the physical beacon's UUID/Major/Minor first (via the device's setup app), then enter the same values here.
            </div>
          )}
          {isSuperAdmin && superAdminSelectedOrgId && (
            <div style={{ fontSize: "12px", fontWeight: 500, color: ACCENT, marginTop: "6px" }}>
              Registering for: {superAdminSelectedOrgData?.org_name || "Selected Organization"}
            </div>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1.5, px: 3 }}>
          {/* Paste-and-parse: avoids retyping values transcribed from a BLE
              scanner app (e.g. nRF Connect), which is error-prone by hand. */}
          <FormField>
            {!showPasteScan ? (
              <div
                onClick={() => setShowPasteScan(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  border: `1px dashed ${ACCENT}`,
                  backgroundColor: "#fff7f0",
                  color: ACCENT,
                  fontSize: "12.5px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <ClipboardPaste size={15} />
                Paste scan data from nRF Connect to auto-fill UUID/Major/Minor
              </div>
            ) : (
              <div
                style={{
                  borderRadius: "10px",
                  border: `1px solid ${ACCENT}`,
                  backgroundColor: "#fff7f0",
                  padding: "12px 14px",
                }}
              >
                <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: "8px" }}>
                  <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#9a3412" }}>
                    Paste beacon scan details
                  </span>
                  <IconButton size="small" onClick={() => { setShowPasteScan(false); setPasteScanText(""); }}>
                    <X size={14} />
                  </IconButton>
                </div>
                <textarea
                  autoFocus
                  value={pasteScanText}
                  onChange={(e) => setPasteScanText(e.target.value)}
                  placeholder={`Paste anything containing the UUID/Major/Minor, e.g.:\nUUID: E4CCDBE2-2A2A-2FA3-E911-F3CDF8B4DDCC\nMajor: 1\nMinor: 1`}
                  style={{
                    width: "100%",
                    minHeight: "72px",
                    fontFamily: "monospace",
                    fontSize: "12px",
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "1px solid #e0c4a8",
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                />
                <div className="d-flex justify-content-end" style={{ marginTop: "8px" }}>
                  <ArthaContinueButton onClick={handleApplyPastedScan} disabled={!pasteScanText.trim()}>
                    Extract &amp; Fill
                  </ArthaContinueButton>
                </div>
              </div>
            )}
          </FormField>

          <FormField>
            <ArthaTextField
              autoFocus
              fullWidth
              sx={dialogFieldSx}
              label="Beacon Name"
              name="beacon_name"
              value={formData.beacon_name}
              onChange={handleInputChange}
              placeholder="e.g. Location 101 Beacon"
            />
          </FormField>

          <FormField>
            <ArthaTextField
              fullWidth
              sx={dialogFieldSx}
              label="UUID"
              name="uuid"
              value={formData.uuid}
              onChange={handleInputChange}
              placeholder="E4CCDBE2-2A2A-2FA3-E911-F3CDF8B4DDCC"
              error={
                Boolean(duplicateBeacon) ||
                (uuidTouched && formData.uuid.trim() !== "" && !isUuidValid)
              }
              helperText={
                duplicateBeacon
                  ? `Already registered as "${duplicateBeacon.beacon_name}" (same UUID, Major and Minor)`
                  : uuidTouched && formData.uuid.trim() !== "" && !isUuidValid
                  ? "Must be a standard UUID: 8-4-4-4-12 hex characters"
                  : "Must match the UUID the physical beacon is broadcasting"
              }
            />
          </FormField>

          <FormField>
            {/* Equal-width flex row with a fixed gap, so both inputs line up with
                the full-width fields above and below. */}
            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ArthaTextField
                  fullWidth
                  sx={dialogFieldSx}
                  type="number"
                  label="Major (Optional)"
                  name="major"
                  value={formData.major}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, max: 65535 }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ArthaTextField
                  fullWidth
                  sx={dialogFieldSx}
                  type="number"
                  label="Minor (Optional)"
                  name="minor"
                  value={formData.minor}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, max: 65535 }}
                />
              </div>
            </div>
          </FormField>

          <FormField>
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  fontSize: "0.875rem", backgroundColor: "white", px: 0.5, color: "#666",
                  transform: 'translate(14px, 10px) scale(1)',
                  '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
                  "&.Mui-focused": { color: ACCENT },
                }}
              >
                Location
              </InputLabel>
              <Select
                name="location_id"
                value={formData.location_id || ''}
                onChange={handleInputChange}
                label="Location"
                size="small"
                sx={{
                  fontSize: "0.875rem", height: '40px',
                  "& .MuiSelect-select": { padding: '8px 12px' },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d0d0d0" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#999" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT, borderWidth: '2px' },
                }}
                MenuProps={{ PaperProps: { sx: { mt: 1, maxHeight: 240, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)', borderRadius: '8px' } } }}
              >
                <MenuItem value="">
                  <em>No Location</em>
                </MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc.location_id} value={loc.location_id}>
                    {loc.location_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormField>

          {/* Room/Area dropdown - only once a Location is picked, since a
              Room/Area belongs to exactly one Location. A single Location
              can have multiple Rooms/Areas, each registered with its own
              beacon(s) separately. */}
          {formData.location_id && (
            <FormField>
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    fontSize: "0.875rem", backgroundColor: "white", px: 0.5, color: "#666",
                    transform: 'translate(14px, 10px) scale(1)',
                    '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
                    "&.Mui-focused": { color: ACCENT },
                  }}
                >
                  Room/Area (Optional)
                </InputLabel>
                <Select
                  name="room_id"
                  value={formData.room_id || ''}
                  onChange={handleInputChange}
                  label="Room/Area (Optional)"
                  size="small"
                  sx={{
                    fontSize: "0.875rem", height: '40px',
                    "& .MuiSelect-select": { padding: '8px 12px' },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d0d0d0" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#999" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT, borderWidth: '2px' },
                  }}
                  MenuProps={{ PaperProps: { sx: { mt: 1, maxHeight: 240, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)', borderRadius: '8px' } } }}
                >
                  <MenuItem value="">
                    <em>No Room/Area</em>
                  </MenuItem>
                  {getRoomsForLocation(formData.location_id).map((room) => (
                    <MenuItem key={room.beacon_room_id} value={room.beacon_room_id}>
                      {room.room_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!showAddRoom ? (
                <div
                  onClick={() => setShowAddRoom(true)}
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: ACCENT,
                    cursor: "pointer",
                  }}
                >
                  + Add a new Room/Area under this Location
                </div>
              ) : (
                <div className="d-flex align-items-center" style={{ gap: "8px", marginTop: "8px" }}>
                  <ArthaTextField
                    fullWidth
                    sx={dialogFieldSx}
                    size="small"
                    placeholder="e.g. Room 101"
                    value={newRoomName}
                    onChange={(e) => setNewRoomName(e.target.value)}
                  />
                  <ArthaContinueButton onClick={handleAddRoom} disabled={!newRoomName.trim() || isSavingRoom}>
                    Add
                  </ArthaContinueButton>
                  <IconButton size="small" onClick={() => { setShowAddRoom(false); setNewRoomName(""); }}>
                    <X size={14} />
                  </IconButton>
                </div>
              )}
            </FormField>
          )}

          <FormField>
            <ArthaTextField
              fullWidth
              sx={dialogFieldSx}
              type="number"
              label="RSSI Threshold (Optional)"
              name="rssi_threshold"
              value={formData.rssi_threshold}
              onChange={handleInputChange}
              helperText="Sightings weaker than this are ignored, not counted as present. Typical: -60 to -75 = good/reliable (room-level); below -75 to -90 = weak (edge of room/through walls); below -90 = usually unreliable. Default -75."
            />
          </FormField>

          <FormField>
            <ArthaTextField
              fullWidth
              sx={dialogFieldSx}
              type="number"
              label="Required Stay Minutes (Optional)"
              name="required_stay_minutes"
              value={formData.required_stay_minutes}
              onChange={handleInputChange}
              inputProps={{ min: 0, step: 1 }}
              helperText="After a student's presence session starts at this beacon, they are expected to stay near it for this many minutes. If they leave earlier, their managers and admins get an alert. Leave blank to turn this off."
            />
          </FormField>

          <FormField last>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.requires_checkin)}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, requires_checkin: e.target.checked }))
                  }
                  color="primary"
                />
              }
              label="Require identity confirmation when detected"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
            />
            <div style={{ fontSize: 12, lineHeight: 1.4, color: "#6b7280", marginTop: 2 }}>
              When on (default), a student's phone prompts for a one-time face-match
              photo the first time it sees this beacon each day before any sightings
              are recorded. Turn off for low-security areas where photo confirmation
              isn't needed.
            </div>
          </FormField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5, borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
          <ArthaCancelButton onClick={handleCancel} color="primary">
            Cancel
          </ArthaCancelButton>
          <ArthaContinueButton
            onClick={isSuspended ? undefined : handleSave}
            color="primary"
            disabled={isSaveDisabled() || isSuspended}
            style={isSuspended ? { opacity: 0.5, cursor: "not-allowed" } : {}}
            title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
          >
            {isEditing ? "Save Changes" : "Register Beacon"}
          </ArthaContinueButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// Consistent vertical spacing wrapper for every dialog field. ArthaTextField
// and plain MUI FormControl/Select don't produce matching margins with
// margin="normal" (their internal height/padding overrides differ), which
// was collapsing the gap between fields - this wrapper fixes that with an
// explicit, uniform gap regardless of which field type is inside it.
const FormField = ({ children, last }) => (
  <div style={{ marginBottom: last ? 0 : "18px" }}>{children}</div>
);

const StatChip = ({ icon, label, value, warn }) => (
  <div
    className="d-flex align-items-center"
    style={{
      gap: "6px",
      padding: "6px 12px",
      borderRadius: "10px",
      backgroundColor: warn ? "#fff7ed" : "#f5f7fa",
      color: warn ? "#9a3412" : "#444",
      fontSize: "12.5px",
    }}
  >
    {icon}
    <span className="fw-bold">{value}</span>
    <span>{label}</span>
  </div>
);

const MiniStat = ({ label, value }) => (
  <div
    style={{
      fontSize: "11px",
      backgroundColor: "#f5f7fa",
      borderRadius: "6px",
      padding: "3px 8px",
      color: "#555",
    }}
  >
    <span style={{ color: "#999" }}>{label}:</span> <span className="fw-bold">{value}</span>
  </div>
);

export default Beacons;
